import { useState, type FormEvent } from "react";
import { FileUp, Plus } from "lucide-react";
import { actions, listVendors } from "@/lib/api";
import { ImportWizard } from "@/components/import-wizard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useData, matches, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { BuyerSupplierNetwork } from "@/supplier-network";

export function Vendors({ organisation, refresh, query, can, changed }: ViewProps) {
  const { data, error, loading } = useData(
    () => listVendors(organisation.id),
    [organisation.id, refresh],
  );
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [inviting, setInviting] =
    useState<Awaited<ReturnType<typeof listVendors>>[number]>();
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.filter((row) =>
    matches(
      query,
      row.name,
      row.email,
      row.gstin,
      row.tax_identifier,
      row.country_code,
      row.default_currency,
    ),
  );
  return (
    <>
      <PageHeader
        eyebrow="SUPPLIER NETWORK"
        title="Supplier companies"
        description="Manage domestic and international suppliers, commercial defaults and portal access."
      >
        {can("procure") && (
          <>
            <Button className="min-h-11" variant="outline" onClick={() => setImporting(true)}>
              <FileUp className="size-4" aria-hidden="true" />
              Import suppliers
            </Button>
            <Button className="min-h-11" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden="true" />
              Add supplier
            </Button>
          </>
        )}
      </PageHeader>
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "Supplier",
                "Contact",
                "Country / tax",
                "Currency",
                "Terms",
                "Rating",
                "Portal",
              ]}
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.name}</strong></td>
                  <td>{row.email}</td>
                  <td>
                    {row.country_code}
                    <small className="block text-[var(--text-secondary)]">
                      {row.gstin || row.tax_identifier || "Not recorded"}
                    </small>
                  </td>
                  <td>{row.default_currency}</td>
                  <td className="numeric">{row.terms_days} days</td>
                  <td><SupplierRating rating={row.rating} /></td>
                  <td>
                    <SupplierPortalAction
                      portalEnabled={row.portal_enabled}
                      canInvite={can("procure")}
                      onInvite={() => setInviting(row)}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Supplier companies">
            {rows.map((row) => (
              <article
                key={row.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="eyebrow">{row.country_code} · {row.default_currency}</p>
                    <h2 className="mt-1 text-base font-semibold">{row.name}</h2>
                    <p className="mt-1 break-all text-sm text-[var(--text-secondary)]">
                      {row.email}
                    </p>
                  </div>
                  <SupplierRating rating={row.rating} />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Tax identifier</dt>
                    <dd className="mt-0.5">{row.gstin || row.tax_identifier || "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Payment terms</dt>
                    <dd className="mt-0.5 tabular-nums">{row.terms_days} days</dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-[var(--border)] pt-4 [&>button]:min-h-11 [&>button]:w-full">
                  <SupplierPortalAction
                    portalEnabled={row.portal_enabled}
                    canInvite={can("procure")}
                    onInvite={() => setInviting(row)}
                  />
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <Empty
          title="Supplier register is empty"
          description="Add supplier companies before creating an RFQ or contract."
          action={
            can("procure") && (
              <Button onClick={() => setCreating(true)}>Add supplier</Button>
            )
          }
        />
      )}
      <BuyerSupplierNetwork
        organisationId={organisation.id}
        vendors={data}
        canProcure={can("procure") || can("manage")}
        refresh={refresh}
        onChanged={changed}
      />
      <VendorDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        onDone={changed}
      />
      <ImportWizard
        open={importing}
        onOpenChange={setImporting}
        organisationId={organisation.id}
        importKind="suppliers"
        onDone={changed}
      />
      <InviteSupplierDialog
        vendor={inviting}
        onOpenChange={(open) => !open && setInviting(undefined)}
        organisationId={organisation.id}
        onDone={changed}
      />
    </>
  );
}

function SupplierRating({ rating }: { rating: number }) {
  return (
    <span aria-label={`${rating} out of 5 supplier rating`} className="whitespace-nowrap text-sm">
      <span aria-hidden="true">
        {"★".repeat(rating)}{"☆".repeat(5 - rating)}
      </span>
    </span>
  );
}

function SupplierPortalAction({
  portalEnabled,
  canInvite,
  onInvite,
}: {
  portalEnabled: boolean;
  canInvite: boolean;
  onInvite: () => void;
}) {
  if (portalEnabled) return <Badge value="active" />;
  if (!canInvite) return <Badge value="inactive" />;
  return (
    <Button size="sm" variant="outline" onClick={onInvite}>
      Invite supplier
    </Button>
  );
}
function VendorDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: DialogProps) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createVendor(organisationId, {
        name: String(form.get("name")),
        email: String(form.get("email")),
        gstin: String(form.get("gstin") || ""),
        termsDays: Number(form.get("terms")),
        rating: Number(form.get("rating")),
        countryCode: String(form.get("country")).toUpperCase(),
        defaultCurrency: String(form.get("currency")).toUpperCase(),
        taxIdentifier: String(form.get("taxIdentifier") || ""),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="SUPPLIER NETWORK" title="Add supplier company" />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Legal / trade name
              <Input name="name" required />
            </Label>
            <Label>
              Quote email
              <Input name="email" type="email" required />
            </Label>
            <Label>
              Country code
              <Input
                name="country"
                defaultValue="IN"
                pattern="[A-Za-z]{2}"
                maxLength={2}
                required
              />
            </Label>
            <Label>
              Default currency
              <Input
                name="currency"
                defaultValue="INR"
                pattern="[A-Za-z]{3}"
                maxLength={3}
                required
              />
            </Label>
            <Label>
              GSTIN (India)
              <Input name="gstin" maxLength={15} />
            </Label>
            <Label>
              International tax identifier
              <Input name="taxIdentifier" />
            </Label>
            <Label>
              Payment terms
              <Input
                name="terms"
                type="number"
                min="1"
                defaultValue="30"
                required
              />
            </Label>
            <Label>
              Initial rating
              <Select name="rating" defaultValue="3">
                <option value="5">5 — Strategic</option>
                <option value="4">4 — Preferred</option>
                <option value="3">3 — Standard</option>
                <option value="2">2 — Watch</option>
                <option value="1">1 — Restricted</option>
              </Select>
            </Label>
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:min-h-11">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Add supplier
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function InviteSupplierDialog({
  vendor,
  onOpenChange,
  organisationId,
  onDone,
}: {
  vendor?: Awaited<ReturnType<typeof listVendors>>[number];
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!vendor) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.inviteSupplier(organisationId, {
        vendorId: vendor.id,
        email: String(form.get("email")),
        fullName: String(form.get("name")),
        jobTitle: String(form.get("jobTitle") || ""),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(vendor)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="SUPPLIER ACCESS"
          title={`Invite ${vendor?.name || "supplier"}`}
          description="Supabase sends a secure invitation; access is restricted to this supplier company."
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Contact name
            <Input name="name" required />
          </Label>
          <Label>
            Work email
            <Input
              name="email"
              type="email"
              defaultValue={vendor?.email}
              required
            />
          </Label>
          <Label>
            Job title
            <Input name="jobTitle" />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:min-h-11">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Send supplier invitation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
